import React from "react";
import { useNavigate } from "react-router-dom";
import * as Lucide from "lucide-react";

/* Map Feather (mobile) icon names -> lucide-react component names. */
const ICONS = {
  "arrow-left": Lucide.ArrowLeft, "arrow-right": Lucide.ArrowRight,
  "chevron-left": Lucide.ChevronLeft, "chevron-right": Lucide.ChevronRight,
  "chevron-up": Lucide.ChevronUp, "chevron-down": Lucide.ChevronDown,
  "check": Lucide.Check, "check-circle": Lucide.CheckCircle2, "check-square": Lucide.CheckSquare,
  "x": Lucide.X, "x-circle": Lucide.XCircle, "circle": Lucide.Circle, "square": Lucide.Square,
  "user": Lucide.User, "user-plus": Lucide.UserPlus, "user-check": Lucide.UserCheck, "users": Lucide.Users,
  "log-in": Lucide.LogIn, "log-out": Lucide.LogOut, "home": Lucide.Home, "briefcase": Lucide.Briefcase,
  "shield": Lucide.Shield, "key": Lucide.Key, "gift": Lucide.Gift, "credit-card": Lucide.CreditCard,
  "message-circle": Lucide.MessageCircle, "message-square": Lucide.MessageSquare, "send": Lucide.Send,
  "share-2": Lucide.Share2, "refresh-cw": Lucide.RefreshCw, "refresh-ccw": Lucide.RefreshCcw,
  "rotate-ccw": Lucide.RotateCcw, "rotate-cw": Lucide.RotateCw, "alert-circle": Lucide.AlertCircle,
  "alert-triangle": Lucide.AlertTriangle, "edit": Lucide.Pencil, "edit-3": Lucide.PenLine,
  "file-text": Lucide.FileText, "star": Lucide.Star, "calendar": Lucide.Calendar, "clock": Lucide.Clock,
  "phone-call": Lucide.PhoneCall, "loader": Lucide.Loader, "plus": Lucide.Plus, "plus-circle": Lucide.PlusCircle,
  "camera": Lucide.Camera, "image": Lucide.Image, "settings": Lucide.Settings, "external-link": Lucide.ExternalLink,
  "eye": Lucide.Eye, "lock": Lucide.Lock, "inbox": Lucide.Inbox, "book-open": Lucide.BookOpen, "book": Lucide.Book,
  "activity": Lucide.Activity, "grid": Lucide.LayoutGrid, "award": Lucide.Award, "clipboard": Lucide.Clipboard,
  "list": Lucide.List, "filter": Lucide.Filter, "trending-down": Lucide.TrendingDown, "git-merge": Lucide.GitMerge,
  "dollar-sign": Lucide.DollarSign, "bar-chart-2": Lucide.BarChart2, "shuffle": Lucide.Shuffle,
  "thumbs-up": Lucide.ThumbsUp, "link": Lucide.Link, "slash": Lucide.Slash, "save": Lucide.Save, "copy": Lucide.Copy,
  "mail": Lucide.Mail, "repeat": Lucide.Repeat, "trash-2": Lucide.Trash2, "heart": Lucide.Heart,
  "map-pin": Lucide.MapPin, "phone": Lucide.Phone, "zap": Lucide.Zap,
  "help-circle": Lucide.HelpCircle, "menu": Lucide.Menu, "map": Lucide.Map, "sparkles": Lucide.Sparkles,
  "alert-circle": Lucide.AlertCircle,
  "sun": Lucide.Sun, "moon": Lucide.Moon,
};
export function Icon({ name, size = 18, color = "currentColor", strokeWidth = 2 }) {
  const C = ICONS[name] || Lucide.Circle;
  return <C size={size} color={color} strokeWidth={strokeWidth} />;
}

export function Spinner() {
  return <div className="spinner" />;
}
export function Center({ children }) {
  return <div className="center">{children}</div>;
}

export function Button({ label, onClick, variant = "primary", disabled, loading, testID, icon, small, type = "button" }) {
  return (
    <button type={type} data-testid={testID} className={`btn btn-${variant}${small ? " btn-small" : ""}`}
      onClick={onClick} disabled={disabled || loading}>
      {loading ? <Spinner /> : <>{icon ? <Icon name={icon} size={16} /> : null}{label}</>}
    </button>
  );
}

export function Field({ label, value, onChange, placeholder, type = "text", testID, multiline, inputMode, maxLength, autoCapitalize, autoFocus, onEnter, required, error, id }) {
  const gen = React.useId();
  const fid = id || testID || gen;
  const errId = `${fid}-error`;
  const common = {
    id: fid, "data-testid": testID, value: value || "", placeholder, maxLength,
    "aria-invalid": error ? true : undefined, "aria-describedby": error ? errId : undefined,
    "aria-required": required || undefined, onChange: (e) => onChange(e.target.value),
  };
  return (
    <div className="field">
      {label ? <label className="field-label" htmlFor={fid}>{label}{required ? <span className="req" aria-hidden="true"> *</span> : null}</label> : null}
      {multiline ? (
        <textarea className="textarea" {...common} />
      ) : (
        <input className="input" type={type} inputMode={inputMode} autoCapitalize={autoCapitalize} autoFocus={autoFocus}
          onKeyDown={onEnter ? (e) => { if (e.key === "Enter") onEnter(); } : undefined} {...common} />
      )}
      {error ? <p id={errId} className="field-error" role="alert"><Icon name="alert-circle" size={14} /> {error}</p> : null}
    </div>
  );
}

export function Card({ children, testID, className = "" }) {
  return <div className={`card ${className}`} data-testid={testID}>{children}</div>;
}

export function Badge({ label, tone = "neutral", testID }) {
  return <span className={`badge ${tone}`} data-testid={testID}>{label}</span>;
}

export function Chip({ label, selected, onClick, testID }) {
  return (
    <button type="button" data-testid={testID} className={`chip${selected ? " selected" : ""}`} aria-pressed={!!selected} onClick={onClick}>
      {label}
    </button>
  );
}

export function SelectCard({ title, desc, selected, onClick, testID }) {
  return (
    <div data-testid={testID} className={`select-card${selected ? " selected" : ""}`} onClick={onClick}>
      <div style={{ flex: 1 }}>
        <div className="sc-title">{title}</div>
        {desc ? <div className="sc-desc">{desc}</div> : null}
      </div>
      <Icon name={selected ? "check-circle" : "circle"} size={20} color={selected ? "var(--brand)" : "var(--border-strong)"} />
    </div>
  );
}

export function KV({ k, v, mono }) {
  return (
    <div className="kv">
      <span className="k">{k}</span>
      <span className={`v${mono ? " mono" : ""}`}>{v}</span>
    </div>
  );
}

export function Metric({ label, value, testID }) {
  return (
    <div className="metric" data-testid={testID}>
      <div className="mv">{value}</div>
      <div className="ml">{label}</div>
    </div>
  );
}

export function Bar({ label, value, max = 100, display, tone = "brand" }) {
  const pct = Math.max(2, Math.min(100, (value / (max || 1)) * 100));
  const color = tone === "success" ? "var(--success)" : tone === "warning" ? "var(--warning)" : tone === "error" ? "var(--error)" : "var(--brand)";
  return (
    <div className="bar">
      <div className="bar-head"><span className="bar-label">{label}</span><span className="bar-value">{display ?? String(value)}</span></div>
      <div className="bar-track"><div className="bar-fill" style={{ width: `${pct}%`, background: color }} /></div>
    </div>
  );
}

export function ProgressBar({ current, total }) {
  const pct = Math.round((current / total) * 100);
  return (
    <div style={{ margin: "8px 0 16px" }}>
      <div className="progress-track"><div className="progress-fill" style={{ width: `${pct}%` }} /></div>
      <div className="progress-label">STEP {current} / {total}</div>
    </div>
  );
}

export function DataTable({ columns, rows, testID }) {
  return (
    <div className="dtable" data-testid={testID}>
      <table>
        <thead><tr>{columns.map((c) => <th key={c.key} style={{ textAlign: c.align || "left" }}>{c.label}</th>)}</tr></thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i}>{columns.map((c) => <td key={c.key} style={{ textAlign: c.align || "left" }}>{String(r[c.key] ?? "—")}</td>)}</tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function useToast() {
  const [msg, setMsg] = React.useState(null);
  const flash = React.useCallback((m, ms = 3500) => {
    setMsg(m);
    if (ms) setTimeout(() => setMsg(null), ms);
  }, []);
  const node = msg ? <div className="toast" data-testid="toast">{msg}</div> : null;
  return { msg, flash, setMsg, node };
}

/* Inline success confirmation card used inside portals */
export function SuccessCard({ testID, title, message, steps = [], actions = [], onClose }) {
  return (
    <div className="success-card" data-testid={testID}>
      <div className="success-card-mark"><Icon name="check-circle" size={30} color="var(--success)" /></div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <h3 className="success-card-title">{title}</h3>
        {message ? <p className="success-card-msg">{message}</p> : null}
        {steps.length ? <ol className="success-steps">{steps.map((s, i) => <li key={i}>{s}</li>)}</ol> : null}
        {actions.length ? (
          <div className="success-actions">
            {actions.map((a, i) => <Button key={i} testID={a.testID} variant={i === 0 ? undefined : "secondary"} icon={a.icon} label={a.label} onClick={a.onClick} />)}
          </div>
        ) : null}
      </div>
      {onClose ? <button className="success-close" data-testid={testID ? `${testID}-close` : undefined} onClick={onClose} aria-label="Dismiss">×</button> : null}
    </div>
  );
}

/* Page header used inside portals / sub-pages */
export function PageHeader({ title, subtitle, onBack, right }) {
  const nav = useNavigate();
  return (
    <div className="pg-header">
      {onBack ? (
        <button className="icon-btn" data-testid="header-back" onClick={onBack}><Icon name="arrow-left" size={20} /></button>
      ) : null}
      <div style={{ flex: 1 }}>
        <h1>{title}</h1>
        {subtitle ? <div className="sub">{subtitle}</div> : null}
      </div>
      {right}
      <button className="icon-btn" data-testid="header-home" aria-label="Back to home" title="Back to home" onClick={() => nav("/")}><Icon name="home" size={20} /></button>
    </div>
  );
}
